import type { Ref } from "vue";
import { ref } from "vue";
import LxcError from "~/core/models/LxcError.class";
import type { FiltersSelection } from "~/core/models/filters";
import { Filters } from "~/core/models/filters";
import { useFetchLogActionsActionTypes } from "~/modules/log/composables/useFetchLogActionsActionTypes";
import type { TranslatedLogAttribute } from "~/modules/log/models/TranslatedLogAttribute.interface";

interface UseActionFiltersParams {
  businessUnitCode?: number;
  organizationCode?: number;
}

export function useLogActionActionTypesFilters({
  businessUnitCode,
  organizationCode,
}: UseActionFiltersParams) {
  const { fetchAllActions, fetchAllActionTypes } =
    useFetchLogActionsActionTypes({
      businessUnitCode,
      organizationCode,
    });

  const actions: Ref<TranslatedLogAttribute[] | undefined> = ref();
  const actionTypes: Ref<TranslatedLogAttribute[] | undefined> = ref();
  const appliedActions = ref<TranslatedLogAttribute[] | undefined>();
  const appliedActionTypes = ref<TranslatedLogAttribute[] | undefined>();
  const cacheActionAttr = ref<TranslatedLogAttribute[] | LxcError>();
  const cacheActionTypesAttr = ref<TranslatedLogAttribute[] | LxcError>();

  async function initializeCache() {
    const [allActions, allActionTypes] = await Promise.all([
      fetchAllActions(),
      fetchAllActionTypes(),
    ]);
    cacheActionAttr.value = allActions;
    cacheActionTypesAttr.value = allActionTypes;
  }

  async function initializeFromFilters(filters: FiltersSelection) {
    if (!cacheActionAttr.value || !cacheActionTypesAttr.value) {
      await initializeCache();
    }
    // Initialize actions
    if (LxcError.check(cacheActionAttr.value)) {
      actions.value = (filters.get(Filters.LOG_ACTION)?.value as string[]).map(
        (rawValue) => ({ rawValue, translation: rawValue }),
      );
    } else {
      actions.value = cacheActionAttr.value?.filter((action) =>
        (filters.get(Filters.LOG_ACTION)?.value as string[]).includes(
          action.rawValue,
        ),
      );
    }

    // Initialize action types
    if (LxcError.check(cacheActionTypesAttr.value)) {
      actionTypes.value = (
        filters.get(Filters.LOG_ACTION_TYPE)?.value as string[]
      ).map((rawValue) => ({ rawValue, translation: rawValue }));
    } else {
      actionTypes.value = cacheActionTypesAttr.value?.filter((actionType) =>
        (filters.get(Filters.LOG_ACTION_TYPE)?.value as string[]).includes(
          actionType.rawValue,
        ),
      );
    }
  }

  function updateAppliedFilters() {
    appliedActions.value = actions.value;
    appliedActionTypes.value = actionTypes.value;
  }

  function clearFilters() {
    actions.value = [];
    actionTypes.value = [];
  }

  function getFilterValues() {
    return {
      actions: actions.value?.map((t) => t.rawValue) ?? [],
      actionTypes: actionTypes.value?.map((t) => t.rawValue) ?? [],
    };
  }

  return {
    actions,
    actionTypes,
    appliedActions,
    appliedActionTypes,
    initializeFromFilters,
    updateAppliedFilters,
    clearFilters,
    getFilterValues,
  };
}
